import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {MICROSOFT_LOGIN_MUTATION, MicrosoftIcon} from "@khan/auth-microsoft";
import {createReauthUrl, type LoginType} from "@khan/auth-shared";
import {useMutation} from "@khan/data-gql";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {doNotTranslate} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {redirect, type Location} from "@khan/urls";

import RequireReauth from "../require-reauth.tsx";

interface Props {
    appleId: string | null | undefined;
    microsoftId: string | null | undefined;
    microsoftPrimaryEmail: string | null | undefined;
    facebookId: string | null | undefined;
    googlePrimaryEmail: string | null | undefined;
    gqlClient: ApolloGqlClient;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    location: Location;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    setWarningMessage: (message: string | null) => void;
}

export const LinkMicrosoftAccount: React.FC<Props> = ({
    gqlClient,
    microsoftId,
    microsoftPrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    appleId,
    googlePrimaryEmail,
    facebookId,
    renderThirdPartyDisconnectButton,
    location,
}) => {
    const [getLoginRedirect] = useMutation(MICROSOFT_LOGIN_MUTATION);
    // Get redirect URL that will display the success message.
    const reauthUrl = React.useMemo(
        () =>
            createReauthUrl({
                loginType: "microsoft",
                pathname: location.pathname,
            }),
        [location.pathname],
    );

    const handleConnectMicrosoftLogin = React.useCallback(async () => {
        const variables = {
            action: "LINK" as "LINK" | "LOGIN" | "SIGNUP" | null | undefined,
            continueUrl: reauthUrl,
        };
        const result = await getLoginRedirect(variables);
        if (
            !result ||
            result.status !== "success" ||
            result.data.loginWithMicrosoft?.error
        ) {
            let cause = "";
            let error;

            if (result) {
                if (result.status === "error") {
                    error = result.error;
                } else if (
                    result.status === "success" &&
                    result.data.loginWithMicrosoft?.error
                ) {
                    cause = result.data.loginWithMicrosoft.error.code;
                }
            }

            Log.error("Sign in with Microsoft failed", Errors.Internal, {
                cause: error || cause,
            });
            // get redirect URL that will display the error message.
            const reauthUrlError = createReauthUrl({
                loginType: "microsoft",
                pathname: location.pathname,
                error: error ? error.cause : cause,
            });

            redirect(reauthUrlError);
            return;
        }
        if (result.data.loginWithMicrosoft?.redirectUrl) {
            redirect(result.data.loginWithMicrosoft?.redirectUrl);
        }
    }, [getLoginRedirect, reauthUrl, location.pathname]);

    const microsoftEmailWithDisconnect = (
        <View style={styles.row}>
            <LabelMedium style={[styles.verticalCenter, styles.label]}>
                {microsoftPrimaryEmail}
            </LabelMedium>
            <Strut size={spacing.small_12} />
            {renderThirdPartyDisconnectButton("microsoft")}
        </View>
    );

    const connectMicrosoftButton = (
        <RequireReauth
            identifier={identifier}
            showClasslinkAuthProvider={isClasslinkUser}
            onSuccess={handleConnectMicrosoftLogin}
            onFailure={() => {}}
            hasMicrosoftButton={false}
            // We check the other providers if the user has an account
            // registered with them
            hasGoogleButton={!!googlePrimaryEmail}
            hasFacebookButton={!!facebookId}
            hasAppleButton={!!appleId}
        >
            {(invokeReauth) => (
                <Button
                    onClick={() => {
                        setWarningMessage(null);
                        invokeReauth();
                    }}
                    size="small"
                >
                    {t`Connect with Microsoft`}
                </Button>
            )}
        </RequireReauth>
    );

    return (
        <View style={[styles.row, styles.thirdPartyButtonContainer]}>
            <View
                style={[
                    styles.row,
                    styles.thirdPartyButtonContainer,
                    styles.alignLabels,
                ]}
            >
                <View style={[styles.verticalCenter, styles.thirdPartyIcon]}>
                    <MicrosoftIcon ariaHidden={true} />
                </View>
                <LabelMedium style={[styles.verticalCenter, styles.label]}>
                    {doNotTranslate`Microsoft`}
                </LabelMedium>
            </View>
            <Strut size={spacing.medium_16} />
            {microsoftId
                ? microsoftEmailWithDisconnect
                : connectMicrosoftButton}
        </View>
    );
};

const styles = StyleSheet.create({
    row: {
        alignItems: "center",
        flexDirection: "row",
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    thirdPartyIcon: {
        margin: spacing.xSmall_8,
        width: spacing.medium_16,
        height: spacing.medium_16,
    },
    thirdPartyButtonContainer: {
        height: spacing.xLarge_32,
        display: "flex",
        alignItems: "center",
    },
    alignLabels: {
        flex: "0 0 100px",
    },
    label: {
        color: color.offBlack,
    },
});
